import {
  DeliveryChannel,
  GiftCouponForRedemption,
  PendingChargeEstimateChannel,
  PendingChargeEstimateResponse,
} from '@shared/api/giftCoupon/giftCoupon.interface';

/** couponEstimateKey가 필요로 하는 최소 형태 — lean 조회 응답도 그대로 받기 위한 구조 타입 */
export interface EstimateKeySource {
  selectedChoiceUnit?: { _id?: string } | string | null;
  giftItem?:
    | { _id?: string; giftItemInfo?: { _id?: string } | string | null }
    | string
    | null;
}

/** 정가 기준 계산에 추가로 필요한 형태 — 선택유닛(없으면 상품) 정가 */
export interface ListPriceSource extends EstimateKeySource {
  selectedChoiceUnit?: { _id?: string; priceRegular?: number } | string | null;
  giftItem?:
    | {
        _id?: string;
        priceRegular?: number;
        giftItemInfo?: { _id?: string } | string | null;
      }
    | string
    | null;
}

/**
 * 매체별 정가 감액률 — 발주처 계약 할인율(정가 대비).
 * 실제 입력된 최근 발송가 기준 예측과 대조하는 "정가 기준" 모드의 계산 근거이며,
 * 계약 할인율이 없는 매체는 비워둔다(해당 상품은 기존 예측 단가를 그대로 사용).
 */
export const CHANNEL_LIST_DISCOUNT_RATE: Partial<Record<DeliveryChannel, number>> = {
  [DeliveryChannel.Officecon]: 0.12,
  [DeliveryChannel.Addcon]: 0.15,
};

/** "정가-15%" 표기 — 감액률이 정의되지 않은 매체(미분류 포함)는 null */
export function listDiscountLabel(channel: PendingChargeEstimateChannel): string | null {
  const rate = channel ? CHANNEL_LIST_DISCOUNT_RATE[channel] : undefined;
  return rate === undefined ? null : `정가-${Math.round(rate * 100)}%`;
}

/** 쿠폰의 정가 — 선택유닛 정가 우선, 없으면 상품 정가 (둘 다 없으면 null) */
export function couponListPrice(coupon: ListPriceSource): number | null {
  const unit = coupon.selectedChoiceUnit;
  if (unit && typeof unit === 'object' && unit.priceRegular) return unit.priceRegular;
  if (typeof coupon.giftItem === 'object' && coupon.giftItem?.priceRegular) {
    return coupon.giftItem.priceRegular;
  }
  return null;
}

export interface ListPriceUnit {
  /** 선택유닛(없으면 상품) 정가 */
  listPrice: number;
  /** 매체 계약 감액률 (0.15 = 15%) */
  rate: number;
  /** 감액 단가 = 정가 × (1 − rate), 원 단위 반올림 */
  unitPrice: number;
}

export interface ListPriceEstimateBucket {
  /** 정가 기준 총액 — 정가/감액률이 없는 상품은 기존 예측 단가로 폴백해 합산 */
  amount: number;
  /** 위 폴백(기존 단가로 계산)된 건수 */
  fallbackCount: number;
}

export interface ListPriceEstimate {
  /** 상품키 → 정가 기준 단가 (정가·감액률이 모두 있을 때만 존재) */
  byKey: Map<string, ListPriceUnit>;
  /** 매체키(channel ?? 'null') → 정가 기준 집계 */
  byChannel: Map<string, ListPriceEstimateBucket>;
  totalAmount: number;
  totalFallbackCount: number;
}

/**
 * 발송대기 충전 예상액의 "정가 기준" 모드 — 매체 계약 감액률로 다시 계산한다.
 * 건수·상품 구성은 백엔드 예측 응답을 그대로 쓰고, 정가만 대기 쿠폰에서 가져오므로
 * 기존(최근 발송가 기준) 금액과 같은 모수 위에서 1:1 비교된다.
 * 정가를 모르거나 감액률이 없는 매체는 기존 단가로 폴백해 총액을 비교 가능하게 유지한다.
 * (상품키가 info: 단위면 상조별 giftItem 정가가 갈릴 수 있어 먼저 만난 쿠폰의 정가를 쓴다)
 */
export function computeListPriceEstimate(
  estimate: PendingChargeEstimateResponse,
  pendingCoupons: ListPriceSource[],
): ListPriceEstimate {
  const listPriceByKey = new Map<string, number>();
  for (const coupon of pendingCoupons) {
    const key = couponEstimateKey(coupon);
    if (listPriceByKey.has(key)) continue;
    const listPrice = couponListPrice(coupon);
    if (listPrice) listPriceByKey.set(key, listPrice);
  }

  const byKey = new Map<string, ListPriceUnit>();
  const byChannel = new Map<string, ListPriceEstimateBucket>();
  let totalAmount = 0;
  let totalFallbackCount = 0;

  for (const bucket of estimate.channels) {
    const rate = bucket.channel ? CHANNEL_LIST_DISCOUNT_RATE[bucket.channel] : undefined;
    const acc: ListPriceEstimateBucket = { amount: 0, fallbackCount: 0 };
    for (const item of bucket.items) {
      const listPrice = listPriceByKey.get(item.key);
      if (rate !== undefined && listPrice) {
        const unitPrice = Math.round(listPrice * (1 - rate));
        byKey.set(item.key, { listPrice, rate, unitPrice });
        acc.amount += unitPrice * item.count;
      } else {
        acc.amount += item.unitPriceWhole * item.count;
        acc.fallbackCount += item.count;
      }
    }
    byChannel.set(bucket.channel ?? 'null', acc);
    totalAmount += acc.amount;
    totalFallbackCount += acc.fallbackCount;
  }

  return { byKey, byChannel, totalAmount, totalFallbackCount };
}

/**
 * 쿠폰의 충전예측 상품키 — 백엔드 gift-coupon.pending-charge-estimate.ts 와 동일 규칙.
 * (unit: 선택형 choiceUnit → info: 계약 원상품 → item: 폴백)
 */
export function couponEstimateKey(coupon: EstimateKeySource): string {
  if (
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    coupon.selectedChoiceUnit._id
  ) {
    return `unit:${coupon.selectedChoiceUnit._id}`;
  }
  if (typeof coupon.giftItem === 'object' && coupon.giftItem) {
    const info = coupon.giftItem.giftItemInfo;
    if (info && typeof info === 'object' && info._id) {
      return `info:${info._id}`;
    }
    if (coupon.giftItem._id) {
      return `item:${coupon.giftItem._id}`;
    }
  }
  return 'item:unknown';
}

export interface SelectedChargeEstimateBucket {
  channel: PendingChargeEstimateChannel;
  count: number;
  amount: number;
  /** 정가 기준 모드 총액 — 정가·감액률이 없는 건은 기존 단가로 폴백 */
  listAmount: number;
  /** 위 폴백된 건수 */
  listFallbackCount: number;
}

/**
 * 선택한 쿠폰들의 매체별 충전 예상액 — 전체 예측 응답의 상품별 단가를 재적용해
 * 프론트 단독으로 계산한다 (백엔드 재호출 없음).
 * 예측 응답에 없는 키(응답 이후 새로 대기열에 들어온 상품 등)는 미분류 0원으로 잡힌다.
 * listByKey를 넘기면 정가 기준 금액(listAmount)도 함께 집계한다.
 */
export function computeSelectedChargeEstimate(
  estimate: PendingChargeEstimateResponse,
  selectedCoupons: GiftCouponForRedemption[],
  listByKey?: Map<string, ListPriceUnit> | null,
): SelectedChargeEstimateBucket[] {
  const byKey = new Map<
    string,
    { channel: PendingChargeEstimateChannel; unitPriceWhole: number }
  >();
  for (const bucket of estimate.channels) {
    for (const item of bucket.items) {
      byKey.set(item.key, {
        channel: bucket.channel,
        unitPriceWhole: item.unitPriceWhole,
      });
    }
  }

  const buckets = new Map<string, SelectedChargeEstimateBucket>();
  for (const coupon of selectedCoupons) {
    const key = couponEstimateKey(coupon);
    const ref = byKey.get(key);
    const channel = ref?.channel ?? null;
    const bucketKey = channel ?? 'null';
    const bucket = buckets.get(bucketKey) ?? {
      channel,
      count: 0,
      amount: 0,
      listAmount: 0,
      listFallbackCount: 0,
    };
    const unitPriceWhole = ref?.unitPriceWhole ?? 0;
    const listUnit = listByKey?.get(key);
    bucket.count += 1;
    bucket.amount += unitPriceWhole;
    bucket.listAmount += listUnit ? listUnit.unitPrice : unitPriceWhole;
    if (!listUnit) bucket.listFallbackCount += 1;
    buckets.set(bucketKey, bucket);
  }

  return Array.from(buckets.values()).sort((a, b) => {
    if (a.channel === null) return 1;
    if (b.channel === null) return -1;
    return b.amount - a.amount;
  });
}
